/*global chrome*/
import React, { useEffect, useState } from 'react';
import { Button, Flex, Alert, } from '@smarterservices/smarter-ui';
import StepInstructions from '../Common/StepInstructions';
import { sendMessageToExtension } from '../../helpers/helpers';
import DualPane from '../DualPane';
import { usePaneViewer } from '../PaneViewer';
import Loader from '../Common/Loader';
import { useAppStore } from '../../stores/app';
import { useLogger } from '@ui/shared/components/Logger';

const ShareScreen = ({ stepData, setIsTaskComplete, inSession, setStepMetadata }) => {
  // manage state if the button is clicked to share
  const [isSharing, setIsSharing] = useState(false);
  // manage state if the screen sharing is being set up after selecting a window to share
  const [isSettingUpShare, setIsSettingUpShare] = useState(false);
  // manage state if recording failed but screen sharing is still active
  const [recordingFailed, setRecordingFailed] = useState(false);
  // track which phase of loading we're in
  const [loadingPhase, setLoadingPhase] = useState('configuring'); // 'configuring' or 'recording'
  // pull in onNext so we can auto advance to next step after share
  const [errorMessage, setErrorMessage] = useState(null);

  const { onNext } = usePaneViewer();
  const { startRecording } = useAppStore();
  const logger = useLogger();

  useEffect(() => {
    const checkIfAlreadyPublished = async () => {

      const isAlreadyPublished = await sendMessageToExtension({
        message: 'sp_background__isTrackPublished',
        trackName: 'screen',
      });
      if (isAlreadyPublished) {
        setIsSharing(true);
        setIsTaskComplete(true);
        // now auto advance to next step since screen has been shared...
        onNext();
      }
    };
    checkIfAlreadyPublished();
  }, []);

  const minimizeAllWindows = async () => {
    const response = await sendMessageToExtension({
      message: 'sp_background__minimizeAllWindows',
    });
  };

  const handleStartRecording = async () => {
    try {
      setIsSettingUpShare(true);
      setLoadingPhase('recording');
      setErrorMessage(null);
      setRecordingFailed(false);

      const result = await startRecording({
        maxRetries: 5,
        onRetry: ({ attempt, maxRetries, backoffDelay, message, error }) => {
          const errorMsg = message || error;
          logger.logError("SessionUI", `Recording start failed, retrying in ${backoffDelay}ms (attempt ${attempt}/${maxRetries})... ${errorMsg || ''}`);
        }
      });

      if (result.success) {
        setIsTaskComplete(true);
        setIsSettingUpShare(false);
        onNext();
        setStepMetadata({});
      } else {
        throw new Error(result.message || 'Recording start failed');
      }

    } catch (error) {
      setIsTaskComplete(false);
      setIsSettingUpShare(false);
      setRecordingFailed(true);
      setErrorMessage("There was a problem starting the recording, please try again. If the problem persists please try to restart onboarding.");
      logger.logError("SessionUI", `Recording start failed...`, error);
    }
  };

  const handleShareScreen = async () => {
    try {
      if (!chrome.runtime || !window.chrome) {
        // Chrome is not enabled... extension may be disabled
        window.location.reload();
        return;
      }

      // Add the query params back to the URL in the event the user needs to relaunch after modifying security settings...
      useAppStore.getState().restoreLaunchParams();

      // Disable the button to share again if the popup is already raised
      setIsSharing(true);
      setLoadingPhase('configuring');

      console.log('Starting screen share...');
      // Start sharing the screen to the extension

      const response = await sendMessageToExtension({
        message: 'sp_background__startScreenShare',
      });

      // Remove the query params from the URL
      useAppStore.getState().removeLaunchParams();

      if (response === null) {
        // no listener is set up on the extension
        window.location.reload();
        return;
      }

      setIsSharing(response);

      // Bring the tab/window back into focus in case it was minimized to find the dialog...
      await sendMessageToExtension({
        message: 'sp_background__focusTab',
        state: "maximized"
      });

      if (response) {
        // Screen was successfully shared, so lets start the recording (not in session, as it would already be started)
        if (!inSession) {
          try {

            setIsSettingUpShare(true);
            setLoadingPhase('recording');

            // Start recording/egress with retry logic
            let recordingStartResponse;
            let retryCount = 0;
            const maxRetries = 5;

            while (retryCount <= maxRetries) {

              recordingStartResponse = await startRecording();

              if (recordingStartResponse.success) {
                break;
              }

              if (retryCount < maxRetries) {
                const backoffDelay = Math.pow(2, retryCount) * 1000;
                logger.logError("SessionUI", `Recording start failed, retrying in ${backoffDelay}ms (attempt ${retryCount + 1}/${maxRetries})...`);
                await new Promise(resolve => setTimeout(resolve, backoffDelay));
                retryCount++;
              } else {
                logger.logError("SessionUI", `Recording start failed...`);
                throw new Error(recordingStartResponse);
              }
            }

            setIsTaskComplete(true);

            // now auto advance to next step since screen has been shared...
            onNext();
            setStepMetadata({});

          } catch (error) {
            setIsTaskComplete(false);
            setIsSettingUpShare(false);
            setRecordingFailed(true);
            setErrorMessage("There was a problem starting the recording, please try again. If the problem persists please try to restart onboarding.");
            logger.logError("SessionUI", `Recording start failed...`, error);
          }
        } else {
          // if we are in session, lets show the next button now.
          onNext();
          setIsTaskComplete(true);
          setIsSettingUpShare(true);
        }
      } else {
        //TODO: We need to decide on what we are going to do here.
        logger.logError("SessionUI", `Screen share failed...`);
        setErrorMessage("There was a problem sharing your screen, please try again. If the problem persists please try to restart onboarding.");
        setIsSettingUpShare(false);
      }
    } catch (error) {
      console.error('Error occurred handling screen share:', { error });
      logger.logError("SessionUI", `Error occurred handling screen share, page reloaded`, error);
      window.location.reload();
    }
  };

  return (
    <DualPane
      leftPane={
        <StepInstructions
          title="Instructions"
          instructions={stepData?.body}
        />
      }
      rightPane={
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {isSettingUpShare ?
            <Loader text={loadingPhase === 'configuring' ? 'Configuring screen sharing...' : 'Starting recording...'} />
            :
            (
              <>
                {isSharing && !recordingFailed ? (
                  <>
                    <StepInstructions
                      title="Screen Sharing Help"
                      instructions={stepData?.screenSharingOpenText}
                    />
                    <Button
                      color="primary"
                      size="medium"
                      onClick={minimizeAllWindows}
                    >
                      {stepData.minimizeAllButtonText}
                    </Button>
                  </>
                ) : isSharing && recordingFailed ? (
                  <>
                    {errorMessage && (
                      <Alert
                        variant="error"
                        title="Error"
                        margin="medium"
                      >
                        {errorMessage}
                      </Alert>
                    )}
                    <Button
                      color="primary"
                      size="medium"
                      margin="medium"
                      onClick={handleStartRecording}
                    >
                      Start Recording
                    </Button>
                  </>
                ) : (
                  <>
                    {errorMessage && (
                      <Alert
                        variant="error"
                        title="Error"
                        margin="medium"
                      >
                        {errorMessage}
                      </Alert>
                    )}
                    <Button
                      color="primary"
                      size="medium"
                      margin="medium"
                      onClick={handleShareScreen}
                      interaction={isSharing ? 'disabled' : 'enabled'}
                    >
                      {stepData.button}
                    </Button>
                  </>
                )}

              </>
            )
          }
        </Flex>
      }
    />
  );
};

export default ShareScreen;